import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdminTradeColumns } from "../../components/admin/AdminTradeColumns";
import { fetchWithAuth } from "../../utils/api";
import { useMediaQuery } from "@mui/material";

const AdminEditTrades = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [changes, setChanges] = useState({});
  const [usernameSearch, setUsernameSearch] = useState("");
  const [dateSearch, setDateSearch] = useState(null);
  const [searching, setSearching] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSearch = async () => {
    if (!usernameSearch.trim() && !dateSearch) {
      setSnackbar({ open: true, message: "Please provide username and/or date." });
      return;
    }

    if (searching) return;
    setSearching(true);

    try {
      const params = new URLSearchParams();
      if (usernameSearch.trim()) params.append("username", usernameSearch.trim());
      if (dateSearch) params.append("trade_date", dayjs(dateSearch).format("YYYY-MM-DD"));

      const results = await fetchWithAuth(`/trades/details?${params.toString()}`);

      if (!results || results.length === 0) {
        setSnackbar({ open: true, message: "No trades found." });
      } else {
        const existingIds = new Set(rows.map((row) => row.trade_id));
        const newTrades = results.filter((t) => !existingIds.has(t.trade_id));

        if (newTrades.length === 0) {
          setSnackbar({ open: true, message: "All results already added." });
        }

        setRows((prev) => [...prev, ...newTrades]);
        setOriginalRows((prev) => [...prev, ...newTrades]);
      }
    } catch (err) {
      console.error("Failed to fetch trades:", err);
      setSnackbar({ open: true, message: "Error fetching trade data." });
    } finally {
      setUsernameSearch("");
      setDateSearch(null);
      setChanges({});
      setSearching(false);
    }
  };

  const handleRowUpdate = (newRow, oldRow) => {
    if (!newRow.trade_date || !/^\d{4}-\d{2}-\d{2}$/.test(newRow.trade_date)) {
      alert("Invalid trade date format (YYYY-MM-DD)");
      return oldRow;
    }

    const original = originalRows.find((r) => r.trade_id === newRow.trade_id);
    const isChanged = ["trade_date", "notes", "is_flagged"]
      .some((field) => newRow[field] !== original?.[field]);

    setChanges((prev) => {
      const updated = { ...prev };
      if (isChanged) {
        updated[newRow.trade_id] = newRow;
      } else {
        delete updated[newRow.trade_id];
      }
      return updated;
    });

    setRows((prev) =>
      prev.map((r) => (r.trade_id === newRow.trade_id ? newRow : r))
    );
    return newRow;
  };

  const handleResetChanges = () => {
    setRows([...originalRows]);
    setChanges({});
  };

  const handleRemove = (trade_id) => {
    setRows((prev) => prev.filter((r) => r.trade_id !== trade_id));
    setOriginalRows((prev) => prev.filter((r) => r.trade_id !== trade_id));
    setChanges((prev) => {
      const updated = { ...prev };
      delete updated[trade_id];
      return updated;
    });
  };

  const handleConfirmSave = async () => {
    const updates = Object.values(changes).map((row) => ({
      trade_id: row.trade_id,
      trade_date: row.trade_date,
      notes: row.notes || "",
      is_flagged: row.is_flagged,
    }));

    try {
      const response = await fetchWithAuth("/trades/update", {
        method: "POST",
        body: updates,
      });

      if (response && typeof response.updated === "number" && response.updated >= 0) {
        setSnackbar({ open: true, message: "Changes saved!" });
        setConfirmOpen(false);
        setChanges({});
        setOriginalRows(rows);
      } else {
        throw new Error("Unexpected response from server.");
      }
    } catch (err) {
      console.error("Failed to save changes:", err);
      setSnackbar({ open: true, message: "Error saving changes." });
    }
  };


  return (
    <>
      {isMobile && (
        <Dialog open fullScreen>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100%"
            textAlign="center"
            px={3}
          >
            <Typography variant="h6" mb={2}>
              oops this is ur fault for using a mobile device. that stuff not built for grids like this.
            </Typography>
            <Typography variant="body1" mb={4}>
              go on desktop or just get outta here
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.href = "/admin"}
            >
              Go Back to Admin Tools
            </Button>
          </Box>
        </Dialog>
      )}

      <Box sx={{ px: 2, py: 1 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: theme.palette.accent.main,
            textAlign: "center",
            mb: 2,
          }}
        >
          admin: edit trades
        </Typography>

        <Box
          sx={{
            maxWidth: "1300px",
            mx: "auto",
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            display="flex"
            gap={2}
            mb={2}
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="center"
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setRows([]);
                setOriginalRows([]);
                setChanges({});
                setUsernameSearch("");
                setDateSearch(null);
              }}
              sx={{ minWidth: 120 }}
            >
              Clear All
            </Button>
            <TextField
              label="Search by Username"
              value={usernameSearch}
              onChange={(e) => setUsernameSearch(e.target.value)}
              fullWidth
              size="small"
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Search by Date"
                value={dateSearch}
                onChange={(newDate) => setDateSearch(newDate)}
                format="YYYY-MM-DD"
                slotProps={{
                  textField: { fullWidth: true, size: "small" }
                }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              type="submit"
              disabled={searching}
              sx={{ minWidth: 120 }}
            >
              {searching ? <CircularProgress size={24} color="inherit" /> : "Search"}
            </Button>
          </Box>

          <Box sx={{ height: 600, width: "100%", mb: 2 }}>
            <DataGrid
              rows={rows}
              rowHeight={49}
              columns={AdminTradeColumns(handleRemove)}
              getRowId={(row) => row.trade_id}
              processRowUpdate={handleRowUpdate}
              disableRowSelectionOnClick
              experimentalFeatures={{ newEditingApi: true }}
              pageSizeOptions={[10]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                border: "1px solid",
                borderColor: theme.palette.divider,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.accent.main,
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell--editing": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            />
          </Box>

          {rows.length > 0 && Object.keys(changes).length > 0 && (
            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="outlined"
                onClick={handleResetChanges}
                color="secondary"
                sx={{ flex: 1 }}
              >
                Reset Changes
              </Button>
              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                sx={{ flex: 1 }}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Changes</DialogTitle>
          <DialogContent dividers>
            <Box maxHeight={300} overflow="auto">
              {Object.values(changes).map((item) => (
                <Box key={item.trade_id} mb={2}>
                  <Typography variant="subtitle1">Trade ID: {item.trade_id}</Typography>
                  <ul>
                    {Object.entries(item).map(([key, val]) => {
                      const oldVal = originalRows.find((r) => r.trade_id === item.trade_id)?.[key];
                      if (val !== oldVal && key !== "trade_id") {
                        return (
                          <li key={key}>
                            {key}: <b>{String(oldVal)}</b> → <b>{String(val)}</b>
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmSave} variant="contained">
              Confirm
            </Button>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    </>
  );
};

export default AdminEditTrades;
